import { ReactElement, ReactNode, useMemo } from 'react';

import Table from '@/common/Table';
import { TableCellProps } from '@/common/Table/TableCell';
import { getClasses } from '@/utils';

export type DynamicCellRenderer = {
  test: (value: string) => boolean;
  render: (value: string) => ReactNode;
};

export type DynamicCellRenderers = Record<string, DynamicCellRenderer>;

export const resolveDynamicCellContent = (children: ReactNode, renderers: DynamicCellRenderers = {}): ReactNode => {
  if (typeof children !== 'string') return children;
  const renderer = Object.values(renderers).find(({ test }) => test(children));
  return renderer ? renderer.render(children) : children;
};

export type DynamicCellProps = Omit<TableCellProps, 'children'> & {
  children?: ReactNode;
  renderers?: DynamicCellRenderers;
};

const DynamicCell = ({ className, children, renderers, ...props }: DynamicCellProps): ReactElement => {
  const content = useMemo(() => resolveDynamicCellContent(children, renderers), [children, renderers]);

  return (
    <Table.Cell {...props} className={getClasses('DynamicCell', className)}>
      {content}
    </Table.Cell>
  );
};

export default DynamicCell;
